import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  User,
  Edit,
  Building,
  CreditCard,
  Camera,
  X,
  Save,
  Mail,
  Phone,
  Calendar,
  MapPin,
  AlertCircle,
  FileText,
  Download,
  Eye,
  EyeOff,
  IndianRupee,
  Plus,
  Trash2,
  Upload,
  Shield,
  Hash,
  Check,
  ShieldCheck,
  Lock,
  Key
} from 'lucide-react';
import { validateForm as validateFormUtil, VALIDATION_RULES } from '../../utils/formValidation';
import profileService from '../../api/services/profileService';
import Button from '../../components/common/Button/Button';
import { API_BASE_URL } from '../../api/config';
import {
  useProfile,
  useDocuments,
  useComplianceDetails,
  useUpdateProfile,
  useUploadDocument,
  useDeleteTempDocument,
  useUploadProfilePicture,
  useDocumentTypes
} from '../../hooks/useProfileQueries';
import { useAuth } from '../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  handleTextInputChange,
  handlePhoneInputChange,
  validateText,
  validatePhoneInput,
  getCharacterCount,
  FIELD_LIMITS
} from '../../utils/inputValidation';
import { toast } from '../../utils/toast';
import { formatDate } from '../../utils/stringUtils';
import { Country, State, City } from 'country-state-city';
import LocationSelect from '../../components/common/LocationSelect/LocationSelect';

const MyProfile = () => {
  /* React Query Hooks */
  const { data: profileData, isLoading: isProfileLoading, refetch: refetchProfile } = useProfile();
  const employeeData = profileData; // Alias for backward compatibility
  const { data: documentsData, isLoading: isDocumentsLoading } = useDocuments();
  const documents = documentsData || [];
  const { data: documentTypesData, isLoading: isTypesLoading } = useDocumentTypes();
  const documentTypes = documentTypesData || [];
  const { refetch: fetchComplianceDetails, isFetching: isComplianceLoading } = useComplianceDetails();

  const updateProfileMutation = useUpdateProfile();
  const uploadDocumentMutation = useUploadDocument();
  const deleteTempDocumentMutation = useDeleteTempDocument();
  const uploadProfilePictureMutation = useUploadProfilePicture();

  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false); // Kept for general page loading stats if needed, or derived
  const [uploadingPicture, setUploadingPicture] = useState(false);
  // employeeData is now derived from profileData, but we might want a local reference if structure matches
  const [errors, setErrors] = useState({});
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  // documents state replaced by documentsData
  const [newDocuments, setNewDocuments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [isAddingDocument, setIsAddingDocument] = useState(false);
  const [newDocTitle, setNewDocTitle] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isProvidingFromList, setIsProvidingFromList] = useState(false);
  const [selectedProvidedDocId, setSelectedProvidedDocId] = useState('');
  const [deletingTempIds, setDeletingTempIds] = useState({});
  // loadingDocuments state replaced by isDocumentsLoading
  const [salaryStructure, setSalaryStructure] = useState(null);
  const [payrollData, setPayrollData] = useState(null);
  const profilePictureInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const dobInputRef = useRef(null);

  // Saved profile data for display
  const [savedProfileData, setSavedProfileData] = useState({
    firstName: '',
    lastName: '',
    position: '',
    department: '',
    employeeId: '',
    reportingManager: null
  });

  useEffect(() => {
    const tabFromQuery = new URLSearchParams(location.search).get('tab');
    const allowedTabs = ['personal', 'employment', 'compliance', 'documents', 'salary'];
    if (tabFromQuery && allowedTabs.includes(tabFromQuery)) {
      setActiveTab(tabFromQuery);
    }
  }, [location.search]);

  const setActiveTabAndSync = (tabId) => {
    setActiveTab(tabId);
    navigate(`/my_profile?tab=${tabId}`, { replace: true });
  };

  // Form state matching AddEmployee structure
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    personalEmail: '',
    personalPhoneNumber: '',
    dateOfBirth: '',
    gender: '',

    // Employment Information
    employeeId: '',
    department: '',
    position: '',
    location: '',
    dateOfJoining: '',
    employmentType: '',
    manager: '',

    // Compliance/Payroll IDs
    pan: '',
    aadhaar: '',
    uan: '',
    esi: '',
    bankAccount: '',
    bankName: '',
    bankBranchName: '',
    ifsc: '',

    // Current Address
    street: '',
    city: null, // Object {name, ...}
    state: null, // Object {name, isoCode, ...}
    country: null, // Object {name, isoCode, ...}
    pincode: '',

    // Permanent Address
    permanentStreet: '',
    permanentCity: null,
    permanentState: null,
    permanentCountry: null,
    permanentPincode: '',
    sameAsCurrentAddress: false
  });

  const [initialFormData, setInitialFormData] = useState(null);
  const [initialManagerName, setInitialManagerName] = useState('');

  const [showComplianceDetails, setShowComplianceDetails] = useState(false);
  const [complianceInfo, setComplianceInfo] = useState({
    pan: '',
    aadhaar: '',
    uan: '',
    esi: '',
    bankAccount: '',
    bankName: '',
    bankBranchName: '',
    ifsc: ''
  });
  const [loadingComplianceDetails, setLoadingComplianceDetails] = useState(false);
  const [hasLoadedFullCompliance, setHasLoadedFullCompliance] = useState(false);

  // Load employee data on mount, user change, or navigation
  // Sync Profile Data to Form
  useEffect(() => {
    if (profileData) {
      const emp = profileData;

      // Format date helper for input fields
      const formatDateForInput = (date) => {
        if (!date) return '';
        try {
          const d = new Date(date);
          if (isNaN(d.getTime())) return '';
          return d.toISOString().split('T')[0];
        } catch (e) {
          return '';
        }
      };

      // Helper to extract address field (handling object/string and fallback to user address)
      const getAddrField = (empObj, field, isObject = false) => {
        // 1. Try personalInfo.currentAddress (where data seems to be in user response)
        let val = empObj.personalInfo?.currentAddress?.[field];

        // 2. Try contactInfo.currentAddress (schema definition)
        if (!val) {
          val = empObj.contactInfo?.currentAddress?.[field];
        }

        // 3. Fallback to root address (User model structure)
        if (!val) {
          val = empObj.address?.[field === 'pincode' ? 'zipCode' : field];
        }

        if (val) {
          if (isObject) {
            return typeof val === 'object' ? val : { name: val };
          }
          return val;
        }

        return isObject ? null : '';
      };

      // Helper for permanent address
      const getPermAddrField = (empObj, field, isObject = false) => {
        // 1. Try personalInfo.permanentAddress
        let val = empObj.personalInfo?.permanentAddress?.[field];

        // 2. Try contactInfo.permanentAddress
        if (!val) {
          val = empObj.contactInfo?.permanentAddress?.[field];
        }

        if (val) {
          if (isObject) {
            return typeof val === 'object' ? val : { name: val };
          }
          return val;
        }
        return isObject ? null : '';
      };

      const getCountryObj = (raw) => {
        if (!raw) return null;
        const name = typeof raw === 'object' ? raw.name : raw;
        const code = typeof raw === 'object' ? (raw.isoCode || raw.iso2 || '') : '';
        const found = Country.getAllCountries().find(c =>
          (code && code.length === 2 && c.isoCode === code) ||
          (c.name === name || c.isoCode === name)
        );
        return found || (name ? { name } : null);
      };

      const getStateObj = (raw, country) => {
        if (!raw) return null;
        const name = typeof raw === 'object' ? raw.name : raw;
        const code = typeof raw === 'object' ? (raw.isoCode || raw.state_code || '') : '';
        if (!country || !country.isoCode) return name ? { name } : null;
        const states = State.getStatesOfCountry(country.isoCode);
        const found = states.find(s =>
          (code && code.length === 2 && s.isoCode === code) ||
          (s.name === name || s.isoCode === name)
        );
        return found || (name ? { name } : null);
      };

      const getCityObj = (raw, country, state) => {
        if (!raw) return null;
        const name = typeof raw === 'object' ? raw.name : raw;
        if (!country || !country.isoCode || !state || !state.isoCode) return name ? { name } : null;
        return City.getCitiesOfState(country.isoCode, state.isoCode).find(c => c.name === name) || (name ? { name } : null);
      };

      const countryObj = getCountryObj(getAddrField(emp, 'country', true));
      const stateObj = getStateObj(getAddrField(emp, 'state', true), countryObj);
      const cityObj = getCityObj(getAddrField(emp, 'city', true), countryObj, stateObj);

      const permCountryObj = getCountryObj(getPermAddrField(emp, 'country', true));
      const permStateObj = getStateObj(getPermAddrField(emp, 'state', true), permCountryObj);
      const permCityObj = getCityObj(getPermAddrField(emp, 'city', true), permCountryObj, permStateObj);

      // Map to form data structure
      const profileFormData = {
        firstName: emp.personalInfo?.firstName || '',
        lastName: emp.personalInfo?.lastName || '',
        email: emp.personalInfo?.email || '',
        mobile: emp.personalInfo?.phone || '',
        personalEmail: emp.personalInfo?.personalEmail || '',
        personalPhoneNumber: emp.personalInfo?.personalPhoneNumber || '',
        dateOfBirth: formatDateForInput(emp.personalInfo?.dateOfBirth),
        gender: emp.personalInfo?.gender || '',
        employeeId: emp.employeeId || '',
        department: emp.employmentInfo?.department || '',
        position: emp.employmentInfo?.position || '',
        location: (typeof emp.employmentInfo?.workLocation === 'object' ? emp.employmentInfo?.workLocation?.name : emp.employmentInfo?.workLocation) || '',
        dateOfJoining: formatDateForInput(emp.employmentInfo?.joinDate),
        employmentType: emp.employmentInfo?.employmentType || '',
        manager: emp.employmentInfo?.reportingManager || 'Not Assigned',
        pan: emp.complianceInfo?.pan || '',
        aadhaar: emp.complianceInfo?.aadhaar || 'Not provided',
        uan: emp.complianceInfo?.uan || '',
        esi: emp.complianceInfo?.esi || '',
        bankAccount: emp.complianceInfo?.bankAccount || 'Not provided',
        bankName: emp.complianceInfo?.bankName || '',
        bankBranchName: emp.complianceInfo?.bankBranchName || '',
        ifsc: emp.complianceInfo?.ifsc || '',

        street: getAddrField(emp, 'street'),
        city: cityObj,
        state: stateObj,
        country: countryObj,
        pincode: getAddrField(emp, 'pincode'),

        permanentStreet: getPermAddrField(emp, 'street'),
        permanentCity: permCityObj,
        permanentState: permStateObj,
        permanentCountry: permCountryObj,
        permanentPincode: getPermAddrField(emp, 'pincode'),
        sameAsCurrentAddress: false // Reset initially, user can toggle
      };

      setFormData(profileFormData);

      setComplianceInfo({
        pan: emp.complianceInfo?.pan || 'Not provided',
        aadhaar: emp.complianceInfo?.aadhaar || 'Not provided',
        uan: emp.complianceInfo?.uan || 'Not provided',
        esi: emp.complianceInfo?.esi || 'Not provided',
        bankAccount: emp.complianceInfo?.bankAccount || 'Not provided',
        bankName: emp.complianceInfo?.bankName || 'Not provided',
        bankBranchName: emp.complianceInfo?.bankBranchName || 'Not provided',
        ifsc: emp.complianceInfo?.ifsc || 'Not provided'
      });

      setShowComplianceDetails(false);
      setHasLoadedFullCompliance(false);

      // Extract and set salary structure data
      const salaryStructureData = emp.salaryInfo || emp.salaryStructure || null;
      setSalaryStructure(salaryStructureData);

      // Set payroll data for display
      setPayrollData({
        ...emp.salaryInfo,
        bankAccount: emp.complianceInfo?.bankAccount || "",
        ifsc: emp.complianceInfo?.ifsc || "",
        bankName: emp.complianceInfo?.bankName || "",
        bankBranchName: emp.complianceInfo?.bankBranchName || "",
      });

      // Update saved profile data for header display
      setSavedProfileData({
        firstName: profileFormData.firstName,
        lastName: profileFormData.lastName,
        position: profileFormData.position,
        department: profileFormData.department,
        employeeId: profileFormData.employeeId,
        reportingManager: emp.employmentInfo?.reportingManager,
        candidateId: emp.candidateId || null
      });

      // Sync with global auth context so header refreshes
      // Only if profile picture changed or name changed significantly
      if (user && (
        user.firstName !== profileFormData.firstName ||
        user.lastName !== profileFormData.lastName ||
        (emp.systemInfo?.profilePicture && user.profilePicture !== emp.systemInfo?.profilePicture)
      )) {
        updateUser({
          ...user,
          firstName: profileFormData.firstName,
          lastName: profileFormData.lastName,
        });
      }

      if (!initialFormData) {
        setInitialFormData(profileFormData);
        let mgrName = '';
        if (emp.employmentInfo?.reportingManager) {
          const mgr = emp.employmentInfo.reportingManager;
          const firstName = mgr.personalInfo?.firstName || mgr.firstName || '';
          const lastName = mgr.personalInfo?.lastName || mgr.lastName || '';
          mgrName = `${firstName} ${lastName}`.trim();
        }
        setInitialManagerName(mgrName || 'Not Assigned');
      }
    }
  }, [profileData, initialFormData]); // removed user dependency to avoid loops, profileData change is enough

  // Address Location States
  const [personalCountryOptions, setPersonalCountryOptions] = useState([]);
  const [personalStateOptions, setPersonalStateOptions] = useState([]);
  const [personalCityOptions, setPersonalCityOptions] = useState([]);

  const [permanentStateOptions, setPermanentStateOptions] = useState([]);
  const [permanentCityOptions, setPermanentCityOptions] = useState([]);

  // Load Countries
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    const formattedCountries = allCountries.map(c => ({
      id: c.isoCode,
      name: c.name,
      isoCode: c.isoCode,
      ...c
    }));
    setPersonalCountryOptions(formattedCountries);
  }, []);

  // Sync personal state options
  useEffect(() => {
    if (formData.country && typeof formData.country === 'object' && formData.country.isoCode) {
      const states = State.getStatesOfCountry(formData.country.isoCode);
      const formattedStates = states.map(s => ({
        id: s.isoCode,
        name: s.name,
        isoCode: s.isoCode,
        countryCode: s.countryCode,
        ...s
      }));
      setPersonalStateOptions(formattedStates);
    } else {
      setPersonalStateOptions([]);
    }
  }, [formData.country]);

  // Sync personal city options
  useEffect(() => {
    if (formData.country && typeof formData.country === 'object' && formData.state && typeof formData.state === 'object') {
      const cities = City.getCitiesOfState(formData.country.isoCode, formData.state.isoCode);
      const formattedCities = cities.map(c => ({
        id: `${c.name}-${c.stateCode}`,
        name: c.name,
        stateCode: c.stateCode,
        countryCode: c.countryCode,
        ...c
      }));
      setPersonalCityOptions(formattedCities);
    } else {
      setPersonalCityOptions([]);
    }
  }, [formData.country, formData.state]);

  // Sync permanent state options
  useEffect(() => {
    if (formData.permanentCountry && typeof formData.permanentCountry === 'object' && formData.permanentCountry.isoCode) {
      const states = State.getStatesOfCountry(formData.permanentCountry.isoCode);
      const formattedStates = states.map(s => ({
        id: s.isoCode,
        name: s.name,
        isoCode: s.isoCode,
        countryCode: s.countryCode,
        ...s
      }));
      setPermanentStateOptions(formattedStates);
    } else {
      setPermanentStateOptions([]);
    }
  }, [formData.permanentCountry]);

  // Sync permanent city options
  useEffect(() => {
    if (formData.permanentCountry && typeof formData.permanentCountry === 'object' && formData.permanentState && typeof formData.permanentState === 'object') {
      const cities = City.getCitiesOfState(formData.permanentCountry.isoCode, formData.permanentState.isoCode);
      const formattedCities = cities.map(c => ({
        id: `${c.name}-${c.stateCode}`,
        name: c.name,
        stateCode: c.stateCode,
        countryCode: c.countryCode,
        ...c
      }));
      setPermanentCityOptions(formattedCities);
    } else {
      setPermanentCityOptions([]);
    }
  }, [formData.permanentCountry, formData.permanentState]);

  // Handle Same Address Toggle
  const handleSameAddressToggle = (e) => {
    const isChecked = e.target.checked;
    setFormData(prev => {
      if (isChecked) {
        return {
          ...prev,
          sameAsCurrentAddress: true,
          permanentStreet: prev.street,
          permanentCity: prev.city,
          permanentState: prev.state,
          permanentCountry: prev.country,
          permanentPincode: prev.pincode
        };
      } else {
        return {
          ...prev,
          sameAsCurrentAddress: false
        };
      }
    });
  };

  // Keep addresses synced if toggle is on
  useEffect(() => {
    if (formData.sameAsCurrentAddress) {
      setFormData(prev => ({
        ...prev,
        permanentStreet: prev.street,
        permanentCity: prev.city,
        permanentState: prev.state,
        permanentCountry: prev.country,
        permanentPincode: prev.pincode
      }));
    }
  }, [
    formData.sameAsCurrentAddress,
    formData.street,
    formData.city,
    formData.state,
    formData.country,
    formData.pincode
  ]);

  // Helper for custom select blur
  const handleCustomBlur = (field, value) => {
    // Add any specific blur logic for custom selects if needed
  };



  const maskSensitiveValue = (value) => {
    if (!value || value === 'Not provided') return value || 'Not provided';
    const str = String(value);
    if (str.length <= 4) {
      return '****';
    }
    const start = str.slice(0, 2);
    const end = str.slice(-2);
    return `${start}${'*'.repeat(Math.max(str.length - 4, 4))}${end}`;
  };

  const getComplianceDisplayValue = useCallback((value) => {
    if (!value || value === 'Not provided') return 'Not provided';
    return showComplianceDetails ? value : maskSensitiveValue(value);
  }, [showComplianceDetails]);

  const handleComplianceVisibilityToggle = async () => {
    if (showComplianceDetails) {
      setShowComplianceDetails(false);
      return;
    }

    if (hasLoadedFullCompliance) {
      setShowComplianceDetails(true);
      return;
    }

    try {
      // Use refetch to get data since enabled is false by default
      const { data: result, isError } = await fetchComplianceDetails();

      if (result) {
        setComplianceInfo({
          pan: result.pan || '',
          aadhaar: result.aadhaar || '',
          uan: result.uan || '',
          esi: result.esi || '',
          bankAccount: result.bankAccount || '',
          bankName: result.bankName || '',
          bankBranchName: result.bankBranchName || '',
          ifsc: result.ifsc || ''
        });
        setHasLoadedFullCompliance(true);
        setShowComplianceDetails(true);
      } else if (isError) {
        toast.error('Failed to load compliance details');
      }
    } catch (error) {

      toast.error('Failed to load compliance details');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };



  const handleInputChange = (e) => {
    const { name, value, type } = e.target;

    // Handle different input types
    if (name === 'mobile' || name === 'phone') {
      const sanitized = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: sanitized }));

      let error = '';
      if (sanitized.length > 0 && sanitized.length < 10) {
        error = 'Mobile number must be exactly 10 digits';
      }
      setErrors(prev => ({ ...prev, [name]: error }));
    } else if ((name === 'pincode' || name === 'permanentPincode') && type === 'text') {
      // Postal code specific handling (allow alphanumeric, max length 15)
      const maxLength = 15;
      if (value.length <= maxLength) {
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
    } else if (type === 'text' && name !== 'email') {
      // Text inputs: firstName, lastName
      const maxLength = FIELD_LIMITS[name] || 50;
      const textValue = handleTextInputChange(e, (newValue) => {
        setFormData(prev => ({ ...prev, [name]: newValue }));
      }, maxLength);
      // Validate text
      const validation = validateText(textValue, maxLength);
      setErrors(prev => ({ ...prev, [name]: validation.error }));
    } else {
      // Date, select, email (read-only)
      setFormData(prev => ({ ...prev, [name]: value }));
      // Clear error for this field
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Personal Information validation with character limits
    if (!formData.firstName || formData.firstName.trim() === '') {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    } else if (formData.firstName.trim().length > 50) {
      newErrors.firstName = 'First name cannot exceed 50 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName.trim())) {
      newErrors.firstName = 'First name can only contain letters and spaces';
    }

    if (!formData.lastName || formData.lastName.trim() === '') {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    } else if (formData.lastName.trim().length > 50) {
      newErrors.lastName = 'Last name cannot exceed 50 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.lastName.trim())) {
      newErrors.lastName = 'Last name can only contain letters and spaces';
    }

    if (!formData.mobile || formData.mobile.trim() === '') {
      newErrors.mobile = 'Mobile number is required';
    } else {
      const cleanVal = formData.mobile.replace(/\D/g, '');
      if (cleanVal.length !== 10) {
        newErrors.mobile = 'Mobile number must be exactly 10 digits';
      }
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      const today = new Date();
      const birthDate = new Date(formData.dateOfBirth);

      if (birthDate > today) {
        newErrors.dateOfBirth = 'Date of birth cannot be in the future';
      }

      // Calculate age
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();
      const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

      if (actualAge > 100) {
        newErrors.dateOfBirth = 'Date of birth seems too old (more than 100 years)';
      }
      if (actualAge < 18) {
        newErrors.dateOfBirth = 'You must be at least 18 years old';
      }
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    // specific validations for fields available in edit mode
    // We only validate these if they are present or required in the strict sense
    // But since this is a profile update, we mainly validate what's provided or visible

    // Compliance/Payroll IDs validation with proper format checking (if editing)
    if (isEditing) {
      // PAN validation - Required
      if (formData.pan && formData.pan !== 'Not provided' && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan.toUpperCase())) {
        newErrors.pan = 'PAN must be in format: ABCDE1234F';
      }

      // Aadhaar validation - Required
      if (formData.aadhaar && formData.aadhaar !== 'Not provided' && !/^\d{12}$/.test(formData.aadhaar)) {
        newErrors.aadhaar = 'Aadhaar must be exactly 12 digits';
      }

      // UAN validation - Optional
      if (formData.uan && formData.uan !== 'Not provided' && !/^\d{12}$/.test(formData.uan)) {
        newErrors.uan = 'UAN must be exactly 12 digits';
      }

      // ESI validation - Optional
      if (formData.esi && formData.esi !== 'Not provided' && !/^\d{10}$/.test(formData.esi)) {
        newErrors.esi = 'ESI must be exactly 10 digits';
      }

      // Bank Account validation - Optional
      if (formData.bankAccount && formData.bankAccount !== 'Not provided' && !/^\d{9,18}$/.test(formData.bankAccount)) {
        newErrors.bankAccount = 'Bank account must be 9-18 digits';
      }

      // IFSC validation - Optional
      if (formData.ifsc && formData.ifsc !== 'Not provided' && formData.ifsc !== 'NOT PROVIDED' && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc.toUpperCase())) {
        newErrors.ifsc = 'IFSC must be in format: ABCD0123456';
      }
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleEditClick = async () => {
    // If we're not yet editing, we might want to load certain things
    if (!isEditing) {
      // Ensure we have full compliance info before editing
      // Always fetch full compliance info when entering edit mode to ensure we have unmasked data
      try {
        const { data: result } = await fetchComplianceDetails();
        if (result) {
          const updatedCompliance = {
            pan: (result.pan === 'Not provided' || result.pan === 'NOT PROVIDED') ? '' : (result.pan || ''),
            aadhaar: (result.aadhaar === 'Not provided' || result.aadhaar === 'NOT PROVIDED') ? '' : (result.aadhaar || ''),
            uan: (result.uan === 'Not provided' || result.uan === 'NOT PROVIDED') ? '' : (result.uan || ''),
            esi: (result.esi === 'Not provided' || result.esi === 'NOT PROVIDED') ? '' : (result.esi || ''),
            bankAccount: (result.bankAccount === 'Not provided' || result.bankAccount === 'NOT PROVIDED') ? '' : (result.bankAccount || ''),
            bankName: (result.bankName === 'Not provided' || result.bankName === 'NOT PROVIDED') ? '' : (result.bankName || ''),
            bankBranchName: (result.bankBranchName === 'Not provided' || result.bankBranchName === 'NOT PROVIDED') ? '' : (result.bankBranchName || ''),
            ifsc: (result.ifsc === 'Not provided' || result.ifsc === 'NOT PROVIDED') ? '' : (result.ifsc || '')
          };
          setFormData(prev => ({
            ...prev,
            ...updatedCompliance
          }));
          setInitialFormData({
            ...formData,
            ...updatedCompliance
          });
          setHasLoadedFullCompliance(true);
        } else {
          setInitialFormData({ ...formData });
        }
      } catch (error) {
        toast.error("Failed to load full compliance details. Some fields may be masked.");
        setInitialFormData({ ...formData });
      }

      let mgrName = '';
      if (profileData?.employmentInfo?.reportingManager) {
        const mgr = profileData.employmentInfo.reportingManager;
        const firstName = mgr.personalInfo?.firstName || mgr.firstName || '';
        const lastName = mgr.personalInfo?.lastName || mgr.lastName || '';
        mgrName = `${firstName} ${lastName}`.trim();
      }
      setInitialManagerName(mgrName || 'Not Assigned');

      setIsEditing(true);
      // Filter tab if on a restricted one
      if (['employment', 'salary'].includes(activeTab)) {
        setActiveTabAndSync('personal');
      }
    }
  };

  const handleCancel = async () => {
    // Delete newly uploaded documents from Cloudinary
    if (newDocuments.length > 0) {
      const confirmMsg = "You have uploaded documents that are not yet saved. These will be deleted from Cloudinary if you cancel. Continue?";
      if (window.confirm(confirmMsg)) {
        setLoading(true);
        try {
          await Promise.all(newDocuments.map(doc =>
            deleteTempDocumentMutation.mutateAsync({
              publicId: doc.publicId,
              resourceType: doc.resourceType
            })
          ));
          toast.info("Unsaved documents cleaned up.");
        } catch (error) {

        } finally {
          setLoading(false);
        }
      } else {
        return; // User cancelled the cancel action
      }
    }

    setIsEditing(false);
    setNewDocuments([]);
    setErrors({});
    setIsAddingDocument(false);
    setNewDocTitle('');
    setSelectedProvidedDocId('');
    setInitialFormData(null);
    refetchProfile(); // Reset form to saved data
  };

  const computeChanges = (initial, current) => {
    if (!initial || !current) return {};
    const changes = {};
    const getValueString = (val) => {
      if (val === null || val === undefined || val === '') return 'Not Provided';

      if (typeof val === 'object' && val !== null) {
        if (val.name) return String(val.name).trim();
        if (val.label) return String(val.label).trim();
        return 'Provided';
      }

      const strVal = String(val).trim();
      const normalized = strVal.toLowerCase().replace(/[\s-_]/g, '');
      if (normalized === 'notprovided') return 'Not Provided';
      return strVal;
    };

    const fieldsToCompare = [
      { key: 'firstName', path: 'personalInfo.firstName' },
      { key: 'lastName', path: 'personalInfo.lastName' },
      { key: 'personalEmail', path: 'contactInfo.personalEmail' },
      { key: 'mobile', path: 'user.phone' },
      { key: 'personalPhoneNumber', path: 'contactInfo.personalPhone' },
      { key: 'dateOfBirth', path: 'personalInfo.dateOfBirth' },
      { key: 'gender', path: 'personalInfo.gender' },
      { key: 'street', path: 'contactInfo.currentAddress.street' },
      { key: 'city', path: 'contactInfo.currentAddress.city' },
      { key: 'state', path: 'contactInfo.currentAddress.state' },
      { key: 'pincode', path: 'contactInfo.currentAddress.pincode' },
      { key: 'country', path: 'contactInfo.currentAddress.country' },
      { key: 'permanentStreet', path: 'contactInfo.permanentAddress.street' },
      { key: 'permanentCity', path: 'contactInfo.permanentAddress.city' },
      { key: 'permanentState', path: 'contactInfo.permanentAddress.state' },
      { key: 'permanentPincode', path: 'contactInfo.permanentAddress.pincode' },
      { key: 'permanentCountry', path: 'contactInfo.permanentAddress.country' },
      { key: 'pan', path: 'complianceInfo.pan' },
      { key: 'aadhaar', path: 'complianceInfo.aadhaar' },
      { key: 'uan', path: 'complianceInfo.uan' },
      { key: 'esi', path: 'complianceInfo.esi' },
      { key: 'bankAccount', path: 'complianceInfo.bankAccount' },
      { key: 'bankName', path: 'complianceInfo.bankName' },
      { key: 'bankBranchName', path: 'complianceInfo.bankBranchName' },
      { key: 'ifsc', path: 'complianceInfo.ifsc' }
    ];

    fieldsToCompare.forEach(item => {
      const initialVal = getValueString(initial[item.key]);
      const currentVal = getValueString(current[item.key]);

      if (initialVal !== currentVal) {
        changes[item.path] = {
          old: initialVal,
          new: currentVal
        };
      }
    });

    return changes;
  };

  const handleSave = async (e) => {
    // Prevent default form submission if called from form
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (!profileData) {
      toast.error(`${user?.role === 'trainee' ? 'Trainee' : 'Employee'} data not loaded. Please refresh the page.`);
      return;
    }

    // Validate form before submission
    const validation = validateForm();
    if (!validation.isValid) {
      // Get the first error message to show specific validation error
      const errorFields = Object.keys(validation.errors).filter(key => validation.errors[key]);
      if (errorFields.length > 0) {
        const firstError = validation.errors[errorFields[0]];
        toast.warning(firstError || 'Please fix the errors in the form before saving.');
      } else {
        toast.warning('Please fill all required fields before saving.');
      }

      // Scroll to first error field for better UX
      setTimeout(() => {
        const firstErrorField = document.querySelector('[aria-invalid="true"]');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstErrorField.focus();
        }
      }, 100);
      return;
    }

    const sanitizeComplianceField = (val) => {
      if (!val) return '';
      const trimmed = val.trim();
      if (trimmed.toLowerCase() === 'not provided') return '';
      return trimmed;
    };

    setLoading(true);
    try {
      // Compute changes and updater metadata for profile update notifications
      const changes = computeChanges(initialFormData, formData);
      const updaterInfo = {
        name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Self',
        employeeId: user?.employeeId || user?.employee_id || 'N/A',
        email: user?.email || 'N/A',
        role: user?.role || 'Self',
        department: user?.department || 'N/A'
      };

      // Prepare update data
      const updateData = {
        changes,
        updaterInfo,
        phone: formData.mobile.trim(),
        personalInfo: {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          dateOfBirth: formData.dateOfBirth || undefined,
          gender: formData.gender,
          phone: formData.mobile.trim()
        },
        contactInfo: {
          personalEmail: formData.personalEmail.trim(),
          personalPhone: formData.personalPhoneNumber.trim(),
          currentAddress: {
            street: formData.street || '',
            city: formData.city || { name: '' },
            state: formData.state || { name: '' },
            country: formData.country || { name: '' },
            pincode: formData.pincode || ''
          },
          permanentAddress: {
            street: formData.permanentStreet || '',
            city: formData.permanentCity || { name: '' },
            state: formData.permanentState || { name: '' },
            country: formData.permanentCountry || { name: '' },
            pincode: formData.permanentPincode || ''
          }
        },
        complianceInfo: {
          pan: sanitizeComplianceField(formData.pan).toUpperCase(),
          aadhaar: sanitizeComplianceField(formData.aadhaar),
          uan: sanitizeComplianceField(formData.uan),
          esi: sanitizeComplianceField(formData.esi),
          bankAccount: sanitizeComplianceField(formData.bankAccount),
          bankName: sanitizeComplianceField(formData.bankName),
          bankBranchName: sanitizeComplianceField(formData.bankBranchName),
          ifsc: sanitizeComplianceField(formData.ifsc).toUpperCase()
        },
        newDocuments: newDocuments // Array of metadata { name, fileUrl, publicId, etc. }
      };



      // Use mutation
      await updateProfileMutation.mutateAsync(updateData);

      // Success handling
      setIsEditing(false);
      setNewDocuments([]);
      setErrors({});
      setInitialFormData(null);

      // Saved profile data state will be updated by the useEffect watching profileData

      // No need to manually reload - React Query invalidation handles it

    } catch (error) {
      // Error handling is mostly done in mutation onError, but we catch here to stop loading state if needed
      // or specific local error handling
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (eOrFile) => {
    let file;
    let isEvent = false;
    if (eOrFile instanceof File) {
      file = eOrFile;
    } else if (eOrFile?.target?.files) {
      file = eOrFile.target.files[0];
      isEvent = true;
    }
    if (!file) return;

    if (!newDocTitle.trim()) {
      toast.error("Please select a document type first.");
      if (isEvent && eOrFile.target) {
        eOrFile.target.value = '';
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validate size (max 25MB)
    const MAX_SIZE = 25 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      toast.error("File size must be less than 25MB");
      if (isEvent && eOrFile.target) {
        eOrFile.target.value = '';
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validate file type (only PDF and images)
    const isImage = file.type.startsWith('image/');
    const isPDF = file.type === 'application/pdf';

    if (!isImage && !isPDF) {
      toast.error("Only PDF and image files (JPG, PNG, GIF, WebP) are allowed.");
      if (isEvent && eOrFile.target) {
        eOrFile.target.value = '';
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Check for duplicate names in both current and new docs
    const allDocNames = [
      ...documents.map(d => (d.name || d.originalName || '').toLowerCase()),
      ...newDocuments.map(d => (d.name || d.originalName || '').toLowerCase())
    ];
    if (allDocNames.includes(file.name.toLowerCase())) {
      toast.error("A document with this name already exists.");
      if (isEvent && eOrFile.target) {
        eOrFile.target.value = '';
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsUploading(true);
    const tempId = `upload-${Date.now()}`;
    setUploadProgress(prev => ({ ...prev, [tempId]: 0 }));

    try {
      const result = await uploadDocumentMutation.mutateAsync({
        file,
        onProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [tempId]: percent }));
        }
      });

      if (result) {
        const newDoc = {
          name: newDocTitle.trim(),
          originalName: file.name,
          filename: result.public_id,
          fileUrl: result.url,
          fileType: file.type,
          fileSize: file.size,
          publicId: result.public_id,
          resourceType: result.resource_type,
          uploadedAt: new Date(),
          tempId: tempId
        };
        setNewDocuments(prev => [...prev, newDoc]);
        toast.success("Document uploaded successfully.");
        setIsAddingDocument(false);
        setNewDocTitle('');
      }
    } catch (error) {
      // Error handled by mutation logic
    } finally {
      setIsUploading(false);
      setUploadProgress(prev => {
        const updated = { ...prev };
        delete updated[tempId];
        return updated;
      });
      // Clear file input
      if (isEvent && eOrFile.target) eOrFile.target.value = '';
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (isUploading) return;
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (isUploading) return;

    if (!newDocTitle) {
      toast.error("Please select a document type first.");
      return;
    }

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await handleFileUpload(files[0]);
    }
  };

  const handleRemoveNewDocument = async (tempId) => {
    const docToRemove = newDocuments.find(d => d.tempId === tempId);
    if (docToRemove) {
      setDeletingTempIds(prev => ({ ...prev, [tempId]: true }));
      try {
        await deleteTempDocumentMutation.mutateAsync({
          publicId: docToRemove.publicId,
          resourceType: docToRemove.resourceType
        });
        setNewDocuments(prev => prev.filter(d => d.tempId !== tempId));
        toast.info("Document removed.");
      } catch (error) {
        // Error handled by mutation or here
        toast.error("Failed to remove document from server.");
      } finally {
        setDeletingTempIds(prev => ({ ...prev, [tempId]: false }));
      }
    }
  };

  const handleUpdateNewDocMetadata = (tempId, field, value) => {
    setNewDocuments(prev => prev.map(d =>
      d.tempId === tempId ? { ...d, [field]: value } : d
    ));
  };

  // Handle keyboard events (WCAG compliance)
  const handleKeyDown = (e) => {
    // Escape key: Cancel editing
    if (e.key === 'Escape' && isEditing) {
      e.preventDefault();
      setIsEditing(false);
      setErrors({});
      refetchProfile();
    }

    // Enter key: Submit form (only when in edit mode and not in textarea)
    if (e.key === 'Enter' && isEditing && e.target.tagName !== 'TEXTAREA') {
      // Don't submit if focus is on Cancel button
      if (e.target.type === 'button' && e.target.textContent === 'Cancel') {
        return;
      }
      e.preventDefault();
      handleSave(e);
    }
  };

  // Handle form submission
  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSave(e);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validatePasswordForm = () => {
    const validationRules = {
      currentPassword: VALIDATION_RULES.required,
      newPassword: {
        required: true,
        minLength: 8
      },
      confirmPassword: VALIDATION_RULES.confirmPassword
    };

    const { errors: valErrors, isValid } = validateFormUtil(passwordForm, validationRules);
    setErrors(valErrors);
    return isValid;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!validatePasswordForm()) return;

    setLoading(true);

    try {
      console.log('🔐 Changing password...');
      const response = await profileService.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });

      if (response.responseCode === 200) {
        toast.success('Password changed successfully!');
        setShowChangePassword(false);
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        toast.error(response.responseMessage || 'Failed to change password');
      }
    } catch (error) {
      console.error('Change password error:', error);
      let errorMessage = 'Failed to change password. Please try again.';

      if (error.response?.data?.responseMessage) {
        errorMessage = error.response.data.responseMessage;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderChangePasswordModal = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-xl border border-slate-100 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-5 border-b border-slate-100 pb-3">
          <div className="flex items-center space-x-2.5">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Key className="w-5 h-5 text-[#0F2942]" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800">Change Password</h3>
          </div>
          <button
            onClick={() => {
              setShowChangePassword(false);
              setErrors({});
              setPasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
              });
            }}
            className="text-gray-400 hover:text-gray-600 bg-gray-50 hover:bg-gray-100 p-1.5 rounded-full transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleChangePassword} className="space-y-4 font-sans">
          <div>
            <label className="block text-[12px] font-medium text-slate-500 mb-1">
              Current Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showCurrentPassword ? "text" : "password"}
                name="currentPassword"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                className={`w-full px-3 py-2 text-[13px] border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 pr-10 ${errors.currentPassword ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
                  }`}
                placeholder="Enter current password"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                {showCurrentPassword ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
            </div>
            {errors.currentPassword && (
              <p className="text-red-500 text-xs mt-1 font-medium">{errors.currentPassword}</p>
            )}
          </div>

          <div>
            <label className="block text-[12px] font-medium text-slate-500 mb-1">
              New Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                name="newPassword"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                className={`w-full px-3 py-2 text-[13px] border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 pr-10 ${errors.newPassword ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
                  }`}
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                {showNewPassword ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
            </div>
            {errors.newPassword && (
              <p className="text-red-500 text-xs mt-1 font-medium">{errors.newPassword}</p>
            )}
            <p className="text-[10px] text-gray-400 mt-1">
              Password must be at least 8 characters
            </p>
          </div>

          <div>
            <label className="block text-[12px] font-medium text-slate-500 mb-1">
              Confirm New Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange}
                className={`w-full px-3 py-2 text-[13px] border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 pr-10 ${errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
                  }`}
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                {showConfirmPassword ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-red-500 text-xs mt-1 font-medium">{errors.confirmPassword}</p>
            )}
          </div>

          <div className="flex space-x-3 pt-4">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#0F2942] hover:bg-[#0B2033] text-white rounded-xl font-semibold text-sm h-11"
            >
              {loading ? 'Changing...' : 'Update'}
            </Button>
            <Button
              type="button"
              onClick={() => {
                setShowChangePassword(false);
                setErrors({});
                setPasswordForm({
                  currentPassword: '',
                  newPassword: '',
                  confirmPassword: ''
                });
              }}
              variant="outline"
              className="flex-1 border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 font-semibold text-sm h-11"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );

  // Handle profile picture upload


  const ensureFilenameHasExtension = (filename, mimeType) => {
    const clean = String(filename || 'document').trim();
    if (/\.[a-zA-Z0-9]+$/.test(clean)) return clean;

    const type = String(mimeType || '').toLowerCase();
    if (type.includes('pdf')) return `${clean}.pdf`;
    if (type === 'application/msword') return `${clean}.doc`;
    if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return `${clean}.docx`;
    if (type.includes('png')) return `${clean}.png`;
    if (type.includes('jpeg') || type.includes('jpg')) return `${clean}.jpg`;
    if (type.includes('gif')) return `${clean}.gif`;
    if (type.includes('webp')) return `${clean}.webp`;
    return clean;
  };

  const getFilenameFromUrl = (url) => {
    try {
      const parsed = new URL(url);
      const lastPart = decodeURIComponent(parsed.pathname.split('/').pop() || '').trim();
      return /\.[a-zA-Z0-9]+$/.test(lastPart) ? lastPart : '';
    } catch (error) {
      return '';
    }
  };

  const triggerBrowserDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getDocumentFileUrl = (doc) => {
    let fileUrl = doc.fileUrl || (doc.url && doc.url !== "#" ? doc.url : null);

    if (!fileUrl && doc.documentData) {
      try {
        const documentDataParsed = typeof doc.documentData === 'string'
          ? JSON.parse(doc.documentData)
          : doc.documentData;

        if (documentDataParsed?.fileUrl) {
          fileUrl = documentDataParsed.fileUrl;
        }
      } catch (parseError) {
        return null;
      }
    }

    return fileUrl;
  };

  const getAuthToken = () => (
    localStorage.getItem('authToken') ||
    localStorage.getItem('auth_token') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('authToken') ||
    sessionStorage.getItem('auth_token') ||
    sessionStorage.getItem('token')
  );

  const getS3KeyFromUrl = (url) => {
    if (!url) return '';
    try {
      const parsed = new URL(url);
      return decodeURIComponent(parsed.pathname.replace(/^\/+/, ''));
    } catch (e) {
      return url;
    }
  };

  const buildS3ProxyUrl = (fileUrl, filename, disposition = 'attachment', contentType = '') => {
    const token = getAuthToken();
    const s3Key = getS3KeyFromUrl(fileUrl);
    const params = new URLSearchParams({
      public_id: s3Key,
      filename,
      disposition
    });
    if (token) params.set('token', token);
    if (contentType) params.set('content_type', contentType);
    return `${API_BASE_URL}/upload/s3Uploader/download?${params.toString()}`;
  };

  const getPreviewErrorMessage = async (response) => {
    try {
      const payload = await response.json();
      return payload.responseMessage || payload.error || payload.message;
    } catch (error) {
      return null;
    }
  };

  const handleViewDocument = async (doc) => {
    const fileUrl = getDocumentFileUrl(doc);

    // Handle base64 data URLs
    if (fileUrl && fileUrl.startsWith('data:')) {
      try {
        const matches = fileUrl.match(/^data:([^;]+);base64,(.+)$/);
        if (matches && matches.length === 3) {
          const mimeType = matches[1];
          const base64Data = matches[2];

          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: mimeType });

          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          return;
        }
      } catch (base64Error) {

      }
    }

    if (fileUrl && fileUrl.includes('amazonaws.com')) {
      const preferredName =
        doc.originalName ||
        doc.filename ||
        doc.name ||
        getFilenameFromUrl(fileUrl) ||
        'document';
      const resolvedFilename = ensureFilenameHasExtension(preferredName, doc.fileType);
      const previewWindow = window.open('', '_blank');
      const previewUrl = buildS3ProxyUrl(fileUrl, resolvedFilename, 'inline', doc.fileType);

      try {
        const response = await fetch(previewUrl);
        if (!response.ok) {
          const message = await getPreviewErrorMessage(response);
          if (previewWindow) previewWindow.close();
          toast.error(message || 'Unable to preview this document. Please re-upload the original file.');
          return;
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        if (previewWindow) {
          previewWindow.location.href = blobUrl;
        } else {
          window.open(blobUrl, '_blank', 'noopener,noreferrer');
        }

        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60 * 1000);
      } catch (error) {
        if (previewWindow) previewWindow.close();
        toast.error('Unable to preview this document. Please try again.');
      }
      return;
    }

    if (fileUrl && (fileUrl.includes('cloudinary.com') || fileUrl.startsWith('http'))) {
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    // If no URL available, show error
    if (!fileUrl) {
      toast.error('Document URL not available. Please contact HR.');
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const fileUrl = getDocumentFileUrl(doc);

      // Handle base64 data URLs
      if (fileUrl && fileUrl.startsWith('data:')) {
        try {
          const matches = fileUrl.match(/^data:([^;]+);base64,(.+)$/);
          if (matches && matches.length === 3) {
            const mimeType = matches[1];
            const base64Data = matches[2];

            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mimeType });

            const url = window.URL.createObjectURL(blob);
            const preferredName = doc.originalName || doc.filename || doc.name || 'document';
            const filename = ensureFilenameHasExtension(preferredName, mimeType);

            triggerBrowserDownload(url, filename);
            setTimeout(() => window.URL.revokeObjectURL(url), 1000);

            toast.success(`Downloaded ${doc.name || doc.originalName || 'document'} successfully!`);
            return;
          }
        } catch (base64Error) {

        }
      }

      // Check if document has Cloudinary URL or HTTP URL
      if (fileUrl && (fileUrl.includes('cloudinary.com') || fileUrl.startsWith('http'))) {
        const preferredName =
          doc.originalName ||
          doc.filename ||
          doc.name ||
          getFilenameFromUrl(fileUrl) ||
          'document';
        const resolvedFilename = ensureFilenameHasExtension(preferredName, doc.fileType);

        try {
          const response = await fetch(fileUrl);
          if (!response.ok) throw new Error('Failed to fetch file');
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const finalFilename = ensureFilenameHasExtension(
            resolvedFilename,
            blob.type || doc.fileType
          );
          triggerBrowserDownload(blobUrl, finalFilename);
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
        } catch (directFetchError) {
          if (!fileUrl.includes('amazonaws.com')) {
            throw directFetchError;
          }
          // Fallback for CORS-restricted file URLs.
          // Use the secure S3 downloader route on our backend to avoid CORS restrictions
          // and ensure the browser downloads the file with the correct filename and extension.
          const downloadUrl = buildS3ProxyUrl(fileUrl, resolvedFilename, 'attachment', doc.fileType);
          triggerBrowserDownload(downloadUrl, resolvedFilename);
        }

        toast.success(`Downloading ${doc.name || doc.originalName || resolvedFilename}...`);
        return;
      }

      if (!fileUrl) {
        toast.error('Document URL not available. Please contact HR to access this document.');
      }
    } catch (error) {

      toast.error(`Failed to download ${doc.name || doc.originalName || 'document'}. Please try again.`);
    }
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingPicture(true);
    try {
      // Upload to Cloudinary
      const uploadResult = await uploadProfilePictureMutation.mutateAsync(file);

      if (uploadResult.success && uploadResult.url) {
        // Update profile with new picture URL
        const updateData = {
          systemInfo: {
            profilePicture: uploadResult.url
          }
        };

        await updateProfileMutation.mutateAsync(updateData);

        // Update local auth user state so header & all components reflect change immediately
        if (user) {
          updateUser({
            ...user,
            profilePicture: uploadResult.url,
            profileImage: uploadResult.url,
            systemInfo: {
              ...(user.systemInfo || {}),
              profilePicture: uploadResult.url
            }
          });
        }
        window.dispatchEvent(new Event('user-profile-updated'));
        toast.success('Profile picture updated successfully!');
      }
    } catch (error) {
      // handled by mutation
    } finally {
      setUploadingPicture(false);
      // Reset input
      if (profilePictureInputRef.current) {
        profilePictureInputRef.current.value = '';
      }
    }
  };

  const handleRemoveProfilePicture = async () => {
    // Show a clean confirmation dialog
    const userConfirmed = await window.confirm('Are you sure you want to remove your profile picture? This will reset it to the default icon.');

    if (!userConfirmed) return;

    setUploadingPicture(true);
    try {
      const updateData = {
        systemInfo: {
          profilePicture: ''
        }
      };

      await updateProfileMutation.mutateAsync(updateData);

      // Update local auth user state so header reflects change
      if (user) {
        updateUser({
          ...user,
          profilePicture: '',
          profileImage: '',
          systemInfo: {
            ...(user.systemInfo || {}),
            profilePicture: ''
          }
        });
      }
      window.dispatchEvent(new Event('user-profile-updated'));

      toast.success('Profile picture removed successfully');
    } catch (error) {

      toast.error('Failed to remove profile picture. Please try again.');
    } finally {
      setUploadingPicture(false);
    }
  };

  const renderPersonalDetails = () => (
    <div className="space-y-6" onKeyDown={handleKeyDown}>
      {/* Profile Header with Photo and Info (SaaS Premium White Card) */}
      <div className="relative overflow-hidden bg-white rounded-3xl shadow-sm border border-slate-200/80 p-6 sm:p-8 mb-6 font-sans">
        {/* Soft background accents */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-bl from-blue-50/60 via-indigo-50/30 to-transparent rounded-full blur-2xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-6">
          {/* Profile Photo */}
          <div className="relative flex-shrink-0">
            <div className="w-24 h-24 rounded-full bg-blue-50/80 flex items-center justify-center overflow-hidden border-4 border-white shadow-md ring-2 ring-blue-100/80">
              {employeeData?.systemInfo?.profilePicture ? (
                <img
                  src={employeeData.systemInfo.profilePicture}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-12 h-12 text-blue-400" />
              )}
            </div>
            {isEditing && (
              <div className="absolute -bottom-2 -right-2 flex flex-col gap-2">
                {employeeData?.systemInfo?.profilePicture && (
                  <button
                    type="button"
                    onClick={handleRemoveProfilePicture}
                    className="bg-red-500 text-white p-2 rounded-full cursor-pointer hover:bg-red-600 shadow-md transition-all flex items-center justify-center transform hover:scale-105"
                    aria-label="Remove profile picture"
                    title="Remove profile picture"
                    disabled={uploadingPicture}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <label
                  className={`bg-[#0F2942] text-white p-2.5 rounded-full cursor-pointer hover:bg-[#0B2033] shadow-md transition-all flex items-center justify-center transform hover:scale-105 ${uploadingPicture ? 'opacity-50 cursor-not-allowed' : ''}`}
                  aria-label="Upload profile picture"
                  title="Upload profile picture"
                >
                  {uploadingPicture ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                  <input
                    ref={profilePictureInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleProfilePictureChange}
                    disabled={uploadingPicture}
                  />
                </label>
              </div>
            )}
          </div>

          {/* Name and Position Info */}
          <div className="flex-1">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div>
                <h2 className="text-2xl lg:text-[26px] font-extrabold text-slate-800 mb-1.5 flex items-center gap-3 tracking-tight">
                  {savedProfileData.firstName} {savedProfileData.lastName}
                  {savedProfileData.employeeId && (
                    <span className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-bold rounded-full border border-blue-200/60 shadow-2xs">
                      ID: {savedProfileData.employeeId}
                    </span>
                  )}
                </h2>
                <p className="text-sm font-semibold text-slate-500 mb-3.5">{savedProfileData.position || 'Position'}</p>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <div className="flex items-center bg-slate-50/90 px-3.5 py-1.5 rounded-xl border border-slate-200/80 text-slate-700 font-semibold">
                    <Building className="w-4 h-4 mr-2 text-blue-600" />
                    <span>{savedProfileData.department || 'Not specified'}</span>
                  </div>
                </div>
              </div>

              {/* Reporting Manager & Change Password */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-slate-50/90 p-4 rounded-2xl border border-slate-200/80">
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">Reporting Manager</span>
                  <div className="flex items-center">
                    <div className="relative">
                      {savedProfileData.reportingManager && typeof savedProfileData.reportingManager === 'object' && savedProfileData.reportingManager.profilePicture ? (
                        <img
                          src={savedProfileData.reportingManager.profilePicture}
                          alt="Manager"
                          className="w-9 h-9 rounded-full border-2 border-white shadow-xs object-cover"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-blue-100/70 flex items-center justify-center border-2 border-white shadow-xs">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                      )}
                      <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full"></div>
                    </div>
                    <div className="ml-3">
                      <p className="text-xs font-bold text-slate-800 leading-none">
                        {savedProfileData.reportingManager && typeof savedProfileData.reportingManager === 'object'
                          ? `${savedProfileData.reportingManager.firstName || ''} ${savedProfileData.reportingManager.lastName || ''}`.trim() || 'Not Assigned'
                          : (savedProfileData.reportingManager || 'Not Assigned')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t sm:border-t-0 sm:border-l border-slate-200/80 pt-3 sm:pt-0 sm:pl-4 self-stretch flex items-center justify-start sm:justify-center">
                  <button
                    type="button"
                    onClick={() => {
                      setErrors({});
                      setPasswordForm({
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: ''
                      });
                      setShowChangePassword(true);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-[#0F2942] hover:bg-[#0A1A2A] active:scale-95 text-white text-xs font-bold rounded-xl shadow-xs transition-all border border-[#0F2942]"
                  >
                    <Key className="w-3.5 h-3.5" />
                    <span>Change Password</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Personal Information Form */}
      <form id="profile-form" onSubmit={handleFormSubmit} className="space-y-5" noValidate>
        {/* Personal Information Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
            <div className="flex items-center">
              <div className="p-2.5 bg-blue-500/10 rounded-xl mr-3 border border-blue-500/20">
                <User className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-slate-900">
                  Personal Information
                </h3>
                <p className="text-xs text-slate-500">Contact details and personal credentials</p>
              </div>
            </div>
            {isEditing && (
              <span className="text-[10px] font-bold px-3 py-1 bg-blue-100 text-blue-800 rounded-full uppercase tracking-wider animate-pulse">
                Editing Mode
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className={isEditing ? '' : 'bg-slate-50/80 rounded-xl p-3.5 border border-slate-100 hover:border-blue-200 hover:bg-blue-50/20 transition-all'}>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                First Name <span className="text-red-500 font-bold ml-0.5">*</span>
              </span>
              {isEditing ? (
                <>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    maxLength={FIELD_LIMITS.firstName}
                    required
                    className={`w-full px-3 py-2 text-xs border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${errors.firstName ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`}
                  />
                  {errors.firstName && <p className="text-xs text-red-600 font-medium mt-1">{errors.firstName}</p>}
                </>
              ) : (
                <span className="text-xs sm:text-sm font-bold text-slate-800">
                  {formData.firstName || <span className="inline-flex items-center text-slate-400 italic text-xs font-medium bg-slate-100 px-2 py-0.5 rounded-md">Not Provided</span>}
                </span>
              )}
            </div>

            <div className={isEditing ? '' : 'bg-slate-50/80 rounded-xl p-3.5 border border-slate-100 hover:border-blue-200 hover:bg-blue-50/20 transition-all'}>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                Last Name <span className="text-red-500 font-bold ml-0.5">*</span>
              </span>
              {isEditing ? (
                <>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    maxLength={FIELD_LIMITS.lastName}
                    required
                    className={`w-full px-3 py-2 text-xs border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${errors.lastName ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`}
                  />
                  {errors.lastName && <p className="text-xs text-red-600 font-medium mt-1">{errors.lastName}</p>}
                </>
              ) : (
                <span className="text-xs sm:text-sm font-bold text-slate-800">
                  {formData.lastName || <span className="inline-flex items-center text-slate-400 italic text-xs font-medium bg-slate-100 px-2 py-0.5 rounded-md">Not Provided</span>}
                </span>
              )}
            </div>

            <div className={isEditing ? '' : 'bg-slate-50/80 rounded-xl p-3.5 border border-slate-100 hover:border-blue-200 hover:bg-blue-50/20 transition-all'}>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Official Email</span>
              <div className="flex items-center space-x-2.5">
                <Mail className="h-4 w-4 text-blue-600 shrink-0" />
                <span className="text-xs sm:text-sm font-bold text-slate-800 break-all">{formData.email}</span>
              </div>
            </div>

            <div className={isEditing ? '' : 'bg-slate-50/80 rounded-xl p-3.5 border border-slate-100 hover:border-blue-200 hover:bg-blue-50/20 transition-all'}>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                Official Mobile <span className="text-red-500 font-bold ml-0.5">*</span>
              </span>
              {isEditing ? (
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  maxLength={10}
                  required
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white"
                />
              ) : (
                <div className="flex items-center space-x-2.5">
                  <Phone className="h-4 w-4 text-blue-600 shrink-0" />
                  <span className="text-xs sm:text-sm font-bold text-slate-800">
                    {formData.mobile || <span className="inline-flex items-center text-slate-400 italic text-xs font-medium bg-slate-100 px-2 py-0.5 rounded-md">Not Provided</span>}
                  </span>
                </div>
              )}
            </div>

            <div className={isEditing ? '' : 'bg-slate-50/80 rounded-xl p-3.5 border border-slate-100 hover:border-blue-200 hover:bg-blue-50/20 transition-all'}>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Personal Email</span>
              {isEditing ? (
                <input
                  type="email"
                  name="personalEmail"
                  value={formData.personalEmail}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white"
                />
              ) : (
                <div className="flex items-center space-x-2.5">
                  <Mail className="h-4 w-4 text-blue-600 shrink-0" />
                  <span className="text-xs sm:text-sm font-bold text-slate-800 break-all">
                    {formData.personalEmail || <span className="inline-flex items-center text-slate-400 italic text-xs font-medium bg-slate-100 px-2 py-0.5 rounded-md">Not Provided</span>}
                  </span>
                </div>
              )}
            </div>

            <div className={isEditing ? '' : 'bg-slate-50/80 rounded-xl p-3.5 border border-slate-100 hover:border-blue-200 hover:bg-blue-50/20 transition-all'}>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Personal Phone Number</span>
              {isEditing ? (
                <input
                  type="tel"
                  name="personalPhoneNumber"
                  value={formData.personalPhoneNumber}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white"
                />
              ) : (
                <div className="flex items-center space-x-2.5">
                  <Phone className="h-4 w-4 text-blue-600 shrink-0" />
                  <span className="text-xs sm:text-sm font-bold text-slate-800">
                    {formData.personalPhoneNumber || <span className="inline-flex items-center text-slate-400 italic text-xs font-medium bg-slate-100 px-2 py-0.5 rounded-md">Not Provided</span>}
                  </span>
                </div>
              )}
            </div>

            <div className={isEditing ? '' : 'bg-slate-50/80 rounded-xl p-3.5 border border-slate-100 hover:border-blue-200 hover:bg-blue-50/20 transition-all'}>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                Date of Birth <span className="text-red-500 font-bold ml-0.5">*</span>
              </span>
              {isEditing ? (
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white"
                />
              ) : (
                <div className="flex items-center space-x-2.5">
                  <Calendar className="h-4 w-4 text-blue-600 shrink-0" />
                  <span className="text-xs sm:text-sm font-bold text-slate-800">
                    {formData.dateOfBirth ? formatDate(formData.dateOfBirth, { includeTime: false }) : <span className="inline-flex items-center text-slate-400 italic text-xs font-medium bg-slate-100 px-2 py-0.5 rounded-md">Not Provided</span>}
                  </span>
                </div>
              )}
            </div>

            <div className={isEditing ? '' : 'bg-slate-50/80 rounded-xl p-3.5 border border-slate-100 hover:border-blue-200 hover:bg-blue-50/20 transition-all'}>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                Gender <span className="text-red-500 font-bold ml-0.5">*</span>
              </span>
              {isEditing ? (
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white"
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              ) : (
                <span className="text-xs sm:text-sm font-bold text-slate-800 capitalize">
                  {formData.gender || <span className="inline-flex items-center text-slate-400 italic text-xs font-medium bg-slate-100 px-2 py-0.5 rounded-md">Not Provided</span>}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Current Address Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
          <div className="flex items-center mb-4">
            <div className="p-1.5 bg-blue-50 rounded-lg mr-3">
              <MapPin className="h-4 w-4 text-[#0F2942]" />
            </div>
            <h3 className="text-[16px] font-medium text-slate-700">
              Current Address
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-[12px] font-medium text-slate-500 mb-1">Street</label>
              {isEditing ? (
                <>
                  <input
                    type="text"
                    name="street"
                    value={formData.street || ''}
                    onChange={handleInputChange}
                    placeholder="Street, building, area"
                    maxLength={FIELD_LIMITS.street}
                    className="w-full px-3 py-2 text-[13px] border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50"
                  />
                  {isEditing && (
                    <p className="mt-1 text-right text-[10px] text-gray-400">
                      {getCharacterCount(formData.street, FIELD_LIMITS.street)} characters
                    </p>
                  )}
                </>
              ) : (
                <div className="text-[13px] font-medium text-slate-700 py-1 px-1 border-b border-slate-100">
                  {formData.street || <span className="text-gray-300 italic">Not provided</span>}
                </div>
              )}
            </div>

            <div>
              <label className="block text-[12px] font-medium text-slate-500 mb-1">Country</label>
              {isEditing ? (
                <LocationSelect
                  options={personalCountryOptions}
                  value={formData.country}
                  onChange={(country) => {
                    setFormData(prev => ({
                      ...prev,
                      country: country,
                      state: null,
                      city: null
                    }));
                  }}
                  placeholder="Select Country"
                />
              ) : (
                <div className="text-[13px] font-medium text-slate-700 py-1 px-1 border-b border-slate-100">
                  {formData.country?.name || <span className="text-gray-300 italic">Not provided</span>}
                </div>
              )}
            </div>

            <div>
              <label className="block text-[12px] font-medium text-slate-500 mb-1">State</label>
              {isEditing ? (
                <LocationSelect
                  options={personalStateOptions}
                  value={formData.state}
                  disabled={!formData.country}
                  onChange={(state) => {
                    setFormData(prev => ({
                      ...prev,
                      state: state,
                      city: null
                    }));
                  }}
                  placeholder="Select State"
                />
              ) : (
                <div className="text-[13px] font-medium text-slate-700 py-1 px-1 border-b border-slate-100">
                  {formData.state?.name || <span className="text-gray-300 italic">Not provided</span>}
                </div>
              )}
            </div>

            <div>
              <label className="block text-[12px] font-medium text-slate-500 mb-1">City</label>
              {isEditing ? (
                <LocationSelect
                  options={personalCityOptions}
                  value={formData.city}
                  disabled={!formData.state}
                  onChange={(city) => {
                    setFormData(prev => ({
                      ...prev,
                      city: city
                    }));
                  }}
                  placeholder="Select City"
                />
              ) : (
                <div className="text-[13px] font-medium text-slate-700 py-1 px-1 border-b border-slate-100">
                  {formData.city?.name || <span className="text-gray-300 italic">Not provided</span>}
                </div>
              )}
            </div>

            <div>
              <label className="block text-[12px] font-medium text-slate-500 mb-1">Postal Code</label>
              {isEditing ? (
                <>
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode || ''}
                    onChange={handleInputChange}
                    placeholder="Postal Code"
                    maxLength={15}
                    className={`w-full px-3 py-2 text-[13px] border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 ${errors.pincode ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.pincode && <p className="text-xs text-red-600 font-medium">{errors.pincode}</p>}
                    <p className="text-[10px] text-gray-400 ml-auto">{(formData.pincode || '').length}/15 chars</p>
                  </div>
                </>
              ) : (
                <div className="text-[13px] font-medium text-slate-700 py-1 px-1 border-b border-slate-100">
                  {formData.pincode || <span className="text-gray-300 italic">Not provided</span>}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Permanent Address Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="p-1.5 bg-blue-50 rounded-lg mr-3">
                <MapPin className="h-4 w-4 text-[#0F2942]" />
              </div>
              <h3 className="text-[16px] font-medium text-slate-700">
                Permanent Address
              </h3>
            </div>
            {isEditing && (
              <label className="flex items-center space-x-2 cursor-pointer select-none px-3 py-1.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <input
                  type="checkbox"
                  checked={formData.sameAsCurrentAddress}
                  onChange={handleSameAddressToggle}
                  className="h-4 w-4 text-[#0F2942] focus:ring-[#0F2942]/50 border-gray-300 rounded"
                />
                <span className="text-[12px] font-medium text-slate-600">Same as Current Address</span>
              </label>
            )}
          </div>

          <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${formData.sameAsCurrentAddress ? 'opacity-75' : ''}`}>
            <div className="md:col-span-2">
              <label className="block text-[12px] font-medium text-slate-500 mb-1">Street Address</label>
              {isEditing && !formData.sameAsCurrentAddress ? (
                <>
                  <input
                    type="text"
                    name="permanentStreet"
                    value={formData.permanentStreet || ''}
                    onChange={handleInputChange}
                    placeholder="Street, building, area"
                    maxLength={FIELD_LIMITS.permanentStreet}
                    className="w-full px-3 py-2 text-[13px] border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50"
                  />
                  <p className="mt-1 text-right text-[10px] text-gray-400">
                    {getCharacterCount(formData.permanentStreet, FIELD_LIMITS.permanentStreet)} characters
                  </p>
                </>
              ) : (
                <div className="text-[13px] font-medium text-slate-700 py-1 px-1 border-b border-slate-100">
                  {formData.permanentStreet || (formData.sameAsCurrentAddress ? formData.street : null) || <span className="text-gray-300 italic">Not provided</span>}
                </div>
              )}
            </div>

            <div>
              <label className="block text-[12px] font-medium text-slate-500 mb-1">Country</label>
              {isEditing && !formData.sameAsCurrentAddress ? (
                <LocationSelect
                  options={personalCountryOptions}
                  value={formData.permanentCountry}
                  onChange={(country) => {
                    setFormData(prev => ({
                      ...prev,
                      permanentCountry: country,
                      permanentState: null,
                      permanentCity: null
                    }));
                  }}
                  placeholder="Select Country"
                />
              ) : (
                <div className="text-[13px] font-medium text-slate-700 py-1 px-1 border-b border-slate-100">
                  {formData.permanentCountry?.name || (formData.sameAsCurrentAddress ? formData.country?.name : null) || <span className="text-gray-300 italic">Not provided</span>}
                </div>
              )}
            </div>

            <div>
              <label className="block text-[12px] font-medium text-slate-500 mb-1">State</label>
              {isEditing && !formData.sameAsCurrentAddress ? (
                <LocationSelect
                  options={permanentStateOptions}
                  value={formData.permanentState}
                  disabled={!formData.permanentCountry}
                  onChange={(state) => {
                    setFormData(prev => ({
                      ...prev,
                      permanentState: state,
                      permanentCity: null
                    }));
                  }}
                  placeholder="Select State"
                />
              ) : (
                <div className="text-[13px] font-medium text-slate-700 py-1 px-1 border-b border-slate-100">
                  {formData.permanentState?.name || (formData.sameAsCurrentAddress ? formData.state?.name : null) || <span className="text-gray-300 italic">Not provided</span>}
                </div>
              )}
            </div>

            <div>
              <label className="block text-[12px] font-medium text-slate-500 mb-1">City</label>
              {isEditing && !formData.sameAsCurrentAddress ? (
                <LocationSelect
                  options={permanentCityOptions}
                  value={formData.permanentCity}
                  disabled={!formData.permanentState}
                  onChange={(city) => {
                    setFormData(prev => ({
                      ...prev,
                      permanentCity: city
                    }));
                  }}
                  placeholder="Select City"
                />
              ) : (
                <div className="text-[13px] font-medium text-slate-700 py-1 px-1 border-b border-slate-100">
                  {formData.permanentCity?.name || (formData.sameAsCurrentAddress ? formData.city?.name : null) || <span className="text-gray-300 italic">Not provided</span>}
                </div>
              )}
            </div>

            <div>
              <label className="block text-[12px] font-medium text-slate-500 mb-1">Postal Code</label>
              {isEditing && !formData.sameAsCurrentAddress ? (
                <>
                  <input
                    type="text"
                    name="permanentPincode"
                    value={formData.permanentPincode || ''}
                    onChange={handleInputChange}
                    placeholder="Postal Code"
                    maxLength={15}
                    className={`w-full px-3 py-2 text-[13px] border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 ${errors.permanentPincode ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.permanentPincode && <p className="text-xs text-red-600 font-medium">{errors.permanentPincode}</p>}
                    <p className="text-[10px] text-gray-400 ml-auto">{(formData.permanentPincode || '').length}/15 chars</p>
                  </div>
                </>
              ) : (
                <div className="text-[13px] font-medium text-slate-700 py-1 px-1 border-b border-slate-100">
                  {formData.permanentPincode || (formData.sameAsCurrentAddress ? formData.pincode : null) || <span className="text-gray-300 italic">Not provided</span>}
                </div>
              )}
            </div>
          </div>
        </div>

      </form>
    </div>
  );

  const renderEmploymentDetails = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
          <div className="flex items-center">
            <div className="p-2.5 bg-blue-500/10 rounded-xl mr-3 border border-blue-500/20">
              <Building className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold text-slate-900">
                Employment Information
              </h3>
              <p className="text-xs text-slate-500">Official organizational records and role assignments</p>
            </div>
          </div>
          <span className="text-[10px] font-bold px-3 py-1 bg-slate-100 text-slate-600 rounded-full uppercase tracking-wider border border-slate-200/60">
            Official Data
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { label: user?.role === 'trainee' ? 'Trainee ID' : 'Employee ID', value: formData.employeeId, icon: Hash },
            { label: 'Department', value: formData.department, icon: Building },
            { label: 'Position', value: formData.position, icon: User },
            { label: 'Location', value: formData.location, icon: MapPin },
            { label: 'Date of Joining', value: formData.dateOfJoining ? formatDate(formData.dateOfJoining, { includeTime: false }) : null, icon: Calendar },
            { label: 'Employment Type', value: formData.employmentType, capitalize: true, icon: FileText },
            { label: 'Reporting Manager', value: typeof formData.manager === 'object' ? `${formData.manager.firstName || ''} ${formData.manager.lastName || ''}`.trim() : formData.manager, icon: User }
          ].map((field, idx) => (
            <div key={idx} className="bg-slate-50/80 rounded-xl p-3.5 border border-slate-100 hover:border-blue-200 hover:bg-blue-50/20 transition-all">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">{field.label}</span>
              <div className="flex items-center space-x-2.5">
                {field.icon && <field.icon className="h-4 w-4 text-blue-600 shrink-0" />}
                <span className={`text-xs sm:text-sm font-bold text-slate-800 ${field.capitalize ? 'capitalize' : ''}`}>
                  {field.value || <span className="inline-flex items-center text-slate-400 italic text-xs font-medium bg-slate-100 px-2 py-0.5 rounded-md">Not Specified</span>}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderComplianceDetails = () => {
    const complianceFields = [
      { label: 'PAN Card Number', name: 'pan', value: isEditing ? (formData.pan === 'Not provided' ? '' : formData.pan) : complianceInfo.pan, icon: CreditCard, limit: 10, required: true },
      { label: 'Aadhaar Number', name: 'aadhaar', value: isEditing ? (formData.aadhaar === 'Not provided' ? '' : formData.aadhaar) : complianceInfo.aadhaar, icon: Shield, limit: 12, required: true },
      { label: 'UAN Number', name: 'uan', value: isEditing ? (formData.uan === 'Not provided' ? '' : formData.uan) : complianceInfo.uan, icon: Hash, limit: 12 },
      { label: 'ESI Number', name: 'esi', value: isEditing ? (formData.esi === 'Not provided' ? '' : formData.esi) : complianceInfo.esi, icon: Hash, limit: 10 },
      { label: 'Bank Name', name: 'bankName', value: isEditing ? (formData.bankName === 'Not provided' ? '' : formData.bankName) : complianceInfo.bankName, icon: Building },
      { label: 'Bank Branch', name: 'bankBranchName', value: isEditing ? (formData.bankBranchName === 'Not provided' ? '' : formData.bankBranchName) : complianceInfo.bankBranchName, icon: MapPin },
      { label: 'Bank Account Number', name: 'bankAccount', value: isEditing ? (formData.bankAccount === 'Not provided' ? '' : formData.bankAccount) : complianceInfo.bankAccount, icon: CreditCard, limit: 18 },
      { label: 'IFSC Code', name: 'ifsc', value: isEditing ? (formData.ifsc === 'Not provided' ? '' : formData.ifsc) : complianceInfo.ifsc, icon: Hash, limit: 11 }
    ];

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
            <div className="flex items-center">
              <div className="p-2.5 bg-blue-500/10 rounded-xl mr-3 border border-blue-500/20">
                <ShieldCheck className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-slate-900">
                  Information & Compliance
                </h3>
                <p className="text-xs text-slate-500">Government identification & statutory tax records</p>
              </div>
            </div>
            {isEditing && (
              <span className="text-[10px] font-bold px-3 py-1 bg-blue-100 text-blue-800 rounded-full uppercase tracking-wider">
                Editing mode
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {complianceFields.map((field, idx) => (
              <div key={idx} className={isEditing ? '' : 'bg-slate-50/80 rounded-xl p-3.5 border border-slate-100 hover:border-blue-200 hover:bg-blue-50/20 transition-all'}>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                  {field.label}
                </span>
                {isEditing ? (
                  <>
                    <div className="relative">
                      {field.icon && <field.icon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />}
                      <input
                        type="text"
                        name={field.name}
                        value={field.value}
                        onChange={handleInputChange}
                        maxLength={field.limit}
                        placeholder={`Enter ${field.label}`}
                        className={`w-full ${field.icon ? 'pl-10' : 'px-3'} pr-3 py-2 text-xs border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${errors[field.name] ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`}
                      />
                    </div>
                  </>
                ) : (
                  <div className="flex items-center space-x-2.5">
                    {field.icon && <field.icon className="h-4 w-4 text-blue-600 shrink-0" />}
                    <span className="text-xs sm:text-sm font-bold text-slate-800">
                      {field.value || <span className="inline-flex items-center text-slate-400 italic text-xs font-medium bg-slate-100 px-2 py-0.5 rounded-md">Not Provided</span>}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const getDocumentDisplayName = (doc) => {
    if (!doc) return 'Untitled Document';
    return doc.name || doc.type || doc.originalName || doc.filename || 'Untitled Document';
  };

  const getDocumentFileName = (doc) => {
    if (!doc) return '';
    return doc.originalName || doc.filename || '';
  };

  const formatDocumentDateTime = (value) => {
    if (!value) return 'N/A';
    const dateObj = new Date(value);
    if (Number.isNaN(dateObj.getTime())) return 'N/A';
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(dateObj);
  };

  const formatDocumentMeta = (doc) => {
    const type = getDocumentTypeLabel(doc);
    const size = getDocumentSizeLabel(doc);
    return `${type} • ${size}`;
  };

  const getFileExtension = (doc) => {
    const candidates = [
      doc?.originalName,
      doc?.filename,
      doc?.name,
      getFilenameFromUrl(doc?.fileUrl || doc?.url || '')
    ].filter(Boolean);
    for (const item of candidates) {
      const match = String(item).toLowerCase().match(/\.([a-z0-9]+)$/);
      if (match?.[1]) return match[1];
    }
    return '';
  };

  const getDocumentTypeLabel = (doc) => {
    const ext = getFileExtension(doc);
    if (ext) return ext.toUpperCase();
    const mime = String(doc?.fileType || '').toLowerCase();
    if (mime.includes('/')) return mime.split('/')[1].toUpperCase();
    return String(doc?.type || 'N/A').toUpperCase();
  };

  const getDocumentSizeLabel = (doc) => {
    const size = doc?.fileSize;
    if (size === null || size === undefined || size === '') return 'N/A';
    const numericSize = typeof size === 'number' ? size : Number(size);
    if (Number.isFinite(numericSize)) {
      if (numericSize <= 0) return '0 KB';
      if (numericSize < 1024 * 1024) {
        return `${Math.max(1, Math.round(numericSize / 1024))} KB`;
      }
      return `${(numericSize / (1024 * 1024)).toFixed(1)} MB`;
    }
    return String(size);
  };

  const getDocumentStatusLabel = (doc) => {
    const raw = doc?.status || doc?.verificationStatus || 'uploaded';
    return String(raw).replace(/_/g, ' ');
  };

  const getDocumentStatusBadgeClass = (doc) => {
    const status = String(doc?.status || doc?.verificationStatus || 'uploaded').toLowerCase();
    if (['uploaded', 'approved', 'verified'].includes(status)) return 'bg-green-100 text-green-700 border-green-200';
    if (['pending', 'in review'].includes(status)) return 'bg-amber-100 text-amber-700 border-amber-200';
    if (['rejected', 'requires_resubmission'].includes(status)) return 'bg-red-100 text-red-700 border-red-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getUploadedTimestamp = (doc) => {
    return formatDocumentDateTime(doc?.uploadedAt || doc?.uploadedDate || doc?.createdAt);
  };

  const renderDocuments = () => {
    // All saved documents are selectable when linking an existing file to a category
    const selectableDocuments = documents;

    // Filter out categories already provided in documents
    const existingTypes = new Set(documents.map(d => (d.type || '').toLowerCase()));
    const remainingTypes = documentTypes.filter(type => {
      const typeName = typeof type === 'string' ? type : (type.name || type.label || '');
      return !existingTypes.has(typeName.toLowerCase());
    });

    const handleSelectProvided = (docId) => {
      const doc = selectableDocuments.find(d => String(d._id || d.id) === String(docId));
      if (doc) {
        const category = newDocTitle.trim();
        // Link this document under the chosen category (type)
        const newDoc = {
          name: category || doc.name,
          type: category || doc.type,
          originalName: doc.originalName || doc.name,
          filename: doc.filename || doc._id,
          fileUrl: doc.fileUrl || doc.url,
          fileType: doc.fileType || 'PDF',
          fileSize: doc.fileSize,
          isLinked: true,
          uploadedAt: new Date(),
          tempId: `link-${Date.now()}`
        };
        setNewDocuments(prev => [...prev, newDoc]);
        setIsAddingDocument(false);
        setNewDocTitle('');
        setIsProvidingFromList(false);
        setSelectedProvidedDocId('');
        toast.success("Document linked successfully.");
      }
    };
    const activeUploadProgress = Object.values(uploadProgress || {})[0] ?? 0;

    return (
      <div className="space-y-4">
        <div className="bg-white rounded-2xl shadow-sm border border-[#E5E7EB] p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#111827] flex items-center">
              <FileText className="h-5 w-5 mr-2 text-[#0F2942]" />
              Documents ({documents.length})
            </h3>
            {isEditing && !isAddingDocument && (
              <button
                type="button"
                onClick={() => setIsAddingDocument(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-[#0F2942] text-white rounded-xl hover:bg-[#0B2033] transition-all font-medium text-sm shadow-sm active:scale-95"
              >
                <Plus className="w-4 h-4" />
                <span>Add Document</span>
              </button>
            )}
          </div>

          {isEditing && isAddingDocument && (
            <div className="mb-6 animate-in fade-in slide-in-from-top-2">
              <div className="bg-[#F9FAFB] p-4 sm:p-6 rounded-xl border border-[#E5E7EB]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">
                      Document Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={newDocTitle}
                      onChange={(e) => setNewDocTitle(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-[#E5E7EB] rounded-xl focus:ring-2 focus:ring-[#0F2942]/50 outline-none transition-all text-sm"
                      disabled={isUploading || isTypesLoading}
                    >
                      <option value="">Select Category</option>
                      {remainingTypes.map((type, index) => {
                        const value = typeof type === 'string' ? type : (type.name || type.label || '');
                        const label = value.charAt(0).toUpperCase() + value.slice(1);
                        return (
                          <option key={type._id || index} value={value}>{label}</option>
                        );
                      })}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Source</label>
                    <div className="flex bg-white rounded-xl p-1 border border-[#E5E7EB]">
                      <button
                        type="button"
                        onClick={() => {
                          setIsProvidingFromList(false);
                          setSelectedProvidedDocId('');
                        }}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${!isProvidingFromList ? 'bg-[#0F2942] text-white shadow-sm' : 'text-[#6B7280] hover:text-[#111827]'}`}
                      >
                        Upload New
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsProvidingFromList(true)}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${isProvidingFromList ? 'bg-[#0F2942] text-white shadow-sm' : 'text-[#6B7280] hover:text-[#111827]'}`}
                      >
                        Select existing
                      </button>
                    </div>
                  </div>
                </div>

                {isProvidingFromList ? (
                  <div className="space-y-4">
                    <p className="text-xs text-[#6B7280]">
                      Choose a document category above, then select a file from your documents below.
                    </p>
                    <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-end">
                      <div className="flex-1 min-w-0">
                        <label className="block text-sm font-semibold text-[#111827] mb-2">Your documents</label>
                        <div className="max-h-56 overflow-y-auto rounded-xl border border-[#E5E7EB] bg-white divide-y divide-[#E5E7EB]">
                          {selectableDocuments.length === 0 ? null : selectableDocuments.map((doc) => {
                            const id = String(doc._id || doc.id || '');
                            const isSelected = selectedProvidedDocId && String(selectedProvidedDocId) === id;
                            return (
                              <button
                                key={id || doc.name}
                                type="button"
                                onClick={() => setSelectedProvidedDocId(id)}
                                className={`w-full text-left px-3 py-2.5 flex items-start gap-3 transition-colors ${isSelected ? 'bg-[#EFF6FF] ring-inset ring-1 ring-[#0F2942]/30' : 'hover:bg-[#F9FAFB]'}`}
                              >
                                <span className={`mt-0.5 h-4 w-4 rounded-full border-2 shrink-0 flex items-center justify-center ${isSelected ? 'border-[#0F2942] bg-[#0F2942]' : 'border-[#D1D5DB]'}`}>
                                  {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                                </span>
                                <span className="min-w-0 flex-1">
                                  <span className="block text-sm font-medium text-[#111827] truncate">
                                    {getDocumentDisplayName(doc)}
                                  </span>
                                  <span className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-[#6B7280]">
                                    <span className={`inline-flex px-1.5 py-0.5 rounded-md border ${getDocumentStatusBadgeClass(doc)}`}>
                                      {getDocumentStatusLabel(doc)}
                                    </span>
                                    {doc.isFromRecruitment && (
                                      <span className="text-[#6B7280]">Recruitment</span>
                                    )}
                                    <span>{getDocumentTypeLabel(doc)}</span>
                                  </span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 shrink-0">
                        {selectedProvidedDocId && (
                          <button
                            type="button"
                            onClick={() => {
                              const doc = selectableDocuments.find(d => String(d._id || d.id) === String(selectedProvidedDocId));
                              if (doc) handleViewDocument(doc);
                            }}
                            className="p-2 bg-[#F3F4F6] text-[#6B7280] rounded-md hover:bg-gray-100 transition-colors"
                            title="Preview"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleSelectProvided(selectedProvidedDocId)}
                          disabled={!selectedProvidedDocId || !newDocTitle}
                          className="flex items-center space-x-2 px-4 py-2 bg-[#0F2942] text-white rounded-xl hover:bg-[#0B2033] disabled:opacity-40 transition-all font-semibold text-sm"
                        >
                          <Check className="w-4 h-4" />
                          <span>Use Selected</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsAddingDocument(false);
                            setNewDocTitle('');
                            setIsProvidingFromList(false);
                            setSelectedProvidedDocId('');
                          }}
                          className="p-2 text-[#6B7280] hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    {selectableDocuments.length === 0 && (
                      <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-xl border border-amber-100">
                        You have no saved documents yet. Use &quot;Upload New&quot; to add a file.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept="application/pdf,image/*"
                        className="hidden"
                      />
                      <div
                        onClick={() => {
                          if (isUploading) return;
                          if (!newDocTitle) {
                            toast.error("Please select a document type first.");
                            return;
                          }
                          fileInputRef.current?.click();
                        }}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        className={`relative border-2 border-dashed rounded-xl p-6 transition-all text-center group ${
                          isUploading 
                            ? 'cursor-not-allowed bg-[#F9FAFB] border-[#E5E7EB]' 
                            : isDragging
                            ? 'border-[#0F2942] bg-blue-50 cursor-pointer shadow-inner'
                            : !newDocTitle
                            ? 'border-gray-200 bg-gray-50/50 cursor-not-allowed opacity-60'
                            : 'border-[#E5E7EB] cursor-pointer hover:border-[#0F2942] hover:bg-blue-50'
                        }`}
                      >
                        {isUploading ? (
                          <div className="flex flex-col items-center justify-center py-2">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F2942] mx-auto"></div>
                            <p className="text-[#111827] font-medium text-sm mt-3">Uploading document...</p>
                            <p className="text-xs text-[#6B7280] mt-1">{activeUploadProgress}% completed</p>
                          </div>
                        ) : !newDocTitle ? (
                          <>
                            <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                            <p className="text-gray-400 font-medium text-sm">Select document type to upload</p>
                            <p className="text-xs text-gray-400 mt-1">Please select a category above to enable upload</p>
                          </>
                        ) : isDragging ? (
                          <>
                            <Upload className="w-8 h-8 text-[#0F2942] mx-auto mb-2 animate-bounce" />
                            <p className="text-[#0F2942] font-semibold text-sm">Drop the file here to upload</p>
                            <p className="text-xs text-[#0F2942]/70 mt-1">Release to start uploading</p>
                          </>
                        ) : (
                          <>
                            <Upload className="w-8 h-8 text-gray-400 group-hover:text-[#0F2942] mx-auto mb-2" />
                            <p className="text-[#111827] font-medium text-sm">Click to upload or drag and drop</p>
                            <p className="text-xs text-[#6B7280] mt-1">PDF, JPG, PNG (Max 25MB)</p>
                          </>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddingDocument(false);
                        setNewDocTitle('');
                        setSelectedProvidedDocId('');
                      }}
                      disabled={isUploading}
                      className="p-2 text-[#6B7280] hover:text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-6 h-6" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {(isDocumentsLoading && documents.length === 0) ? (
            <div className="border border-[#E5E7EB] rounded-xl overflow-hidden">
              <table className="min-w-full">
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <tr>
                    {['Name', 'Type', 'Size', 'Uploaded On', 'Status', 'Actions'].map((head) => (
                      <th key={head} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">{head}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E7EB]">
                  {[...Array(4)].map((_, idx) => (
                    <tr key={`skeleton-${idx}`} className="even:bg-gray-50">
                      <td className="px-4 py-3"><div className="h-4 w-44 bg-gray-200 rounded animate-pulse"></div></td>
                      <td className="px-4 py-3"><div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div></td>
                      <td className="px-4 py-3"><div className="h-4 w-14 bg-gray-200 rounded animate-pulse ml-auto"></div></td>
                      <td className="px-4 py-3"><div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div></td>
                      <td className="px-4 py-3"><div className="h-5 w-20 bg-gray-200 rounded-full animate-pulse mx-auto"></div></td>
                      <td className="px-4 py-3"><div className="h-8 w-20 bg-gray-200 rounded animate-pulse ml-auto"></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (documents.length === 0 && newDocuments.length === 0 && !isAddingDocument) ? (
            <div className="text-center py-12 bg-[#F9FAFB] border border-dashed border-[#E5E7EB] rounded-xl">
              <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border border-[#E5E7EB]">
                <FileText className="h-8 w-8 text-[#6B7280]" />
              </div>
              <p className="text-[#111827] font-semibold text-sm">No documents uploaded</p>
              <p className="text-sm text-[#6B7280] mt-2 max-w-xs mx-auto">
                {isEditing ? "Start by adding your first document." : "Uploaded documents will appear here for your reference."}
              </p>
              {isEditing && (
                <button
                  type="button"
                  onClick={() => setIsAddingDocument(true)}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#0F2942] text-white rounded-xl text-sm font-medium hover:bg-[#0B2033] transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Upload Document
                </button>
              )}
            </div>
          ) : (
            <div className="border border-[#E5E7EB] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Size</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Uploaded On</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB] bg-white">
                    {/* New Documents (Unsaved) */}
                    {newDocuments.map((doc) => (
                      <tr
                        key={doc.tempId}
                        className="even:bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <td className="px-4 py-3 min-w-[240px]">
                          <div className="flex items-start gap-3">
                            <div className="bg-[#DBEAFE] p-2 rounded-lg">
                              <FileText className="h-4 w-4 text-[#0F2942]" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-[#111827] truncate">{doc.name}</p>
                              <p className="text-xs text-[#6B7280] mt-1 truncate">{doc.originalName || 'New document'}</p>
                              {doc.description && String(doc.description).toLowerCase() !== 'uploaded via self service' && (
                                <p className="text-xs text-[#6B7280] mt-1 italic truncate">{doc.description}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#6B7280]">{getDocumentTypeLabel(doc)}</td>
                        <td className="px-4 py-3 text-xs text-[#6B7280] text-right">{getDocumentSizeLabel(doc)}</td>
                        <td className="px-4 py-3 text-xs text-[#6B7280]">{getUploadedTimestamp(doc)}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-tight border bg-[#DBEAFE] text-[#0F2942] border-[#BFDBFE]">
                            Pending Save
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleViewDocument(doc)}
                              className="p-2 rounded-md text-[#6B7280] hover:bg-gray-100 hover:text-[#0F2942] focus:outline-none focus:ring-2 focus:ring-blue-200 cursor-pointer transition-all"
                              title="View"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleRemoveNewDocument(doc.tempId)}
                              disabled={deletingTempIds[doc.tempId]}
                              className="p-2 rounded-md text-[#6B7280] hover:bg-gray-100 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-100 cursor-pointer transition-all disabled:opacity-50"
                              title="Remove"
                            >
                              {deletingTempIds[doc.tempId] ? (
                                <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}

                    {/* Existing Documents */}
                    {documents.map((doc, index) => {
                      const isVerified =
                        doc.isVerified === true ||
                        ['approved', 'verified'].includes(String(doc.verificationStatus || '').toLowerCase()) ||
                        ['approved', 'verified'].includes(String(doc.status || '').toLowerCase());
                      const statusColor = getDocumentStatusBadgeClass(doc);

                      return (
                        <tr
                          key={doc.id || doc._id || index}
                          className="even:bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <td className="px-4 py-3 min-w-[240px]">
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-lg ${isVerified ? 'bg-green-100' : 'bg-[#F3F4F6]'}`}>
                                {isVerified ? (
                                  <ShieldCheck className="h-4 w-4 text-green-600" />
                                ) : (
                                  <FileText className="h-4 w-4 text-[#6B7280]" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-[#111827] truncate">
                                  {getDocumentDisplayName(doc)}
                                </p>
                                {doc.description && String(doc.description).toLowerCase() !== 'uploaded via self service' && (
                                  <p className="text-xs text-[#6B7280] mt-1 italic truncate">{doc.description}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-[#6B7280]">{getDocumentTypeLabel(doc)}</td>
                          <td className="px-4 py-3 text-xs text-[#6B7280] text-right">{getDocumentSizeLabel(doc)}</td>
                          <td className="px-4 py-3 text-xs text-[#6B7280]">{getUploadedTimestamp(doc)}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-tight border ${statusColor}`}>
                              {getDocumentStatusLabel(doc)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleViewDocument(doc)}
                                className="p-2 rounded-md text-[#6B7280] hover:bg-gray-100 hover:text-[#0F2942] focus:outline-none focus:ring-2 focus:ring-blue-200 cursor-pointer transition-all"
                                title="View"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDownloadDocument(doc)}
                                className="p-2 rounded-md text-[#6B7280] hover:bg-gray-100 hover:text-green-600 focus:outline-none focus:ring-2 focus:ring-green-100 cursor-pointer transition-all"
                                title="Download"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                              {isVerified && (
                                <div className="p-2" title="Verified document cannot be modified">
                                  <Lock className="h-4 w-4 text-gray-300" />
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSalaryStructure = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">
        Salary Information
      </h3>

      {salaryStructure ? (
        <>
          {/* Salary Structure Details */}
          <div className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4">
              Salary Structure Details
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="min-w-0">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Structure Name
                </label>
                <p className="text-sm text-gray-900 capitalize break-words">
                  {salaryStructure.name || 'N/A'}
                </p>
              </div>
              <div className="min-w-0">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Structure Code
                </label>
                <p className="text-sm text-gray-900 break-words">
                  {salaryStructure.code || 'N/A'}
                </p>
              </div>
              <div className="min-w-0">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Structure ID
                </label>
                <p className="text-sm text-gray-900 break-words">
                  {salaryStructure.structureId || salaryStructure._id || 'N/A'}
                </p>
              </div>
              <div className="min-w-0">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Department
                </label>
                <p className="text-sm text-gray-900 capitalize break-words">
                  {salaryStructure.department || 'N/A'}
                </p>
              </div>
              <div className="min-w-0">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Grade/Level
                </label>
                <p className="text-sm text-gray-900 capitalize break-words">
                  {salaryStructure.grade || 'N/A'}
                </p>
              </div>
              <div className="min-w-0">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  CTC (Cost to Company)
                </label>
                <p className="text-sm font-semibold text-gray-900 break-words">
                  ₹{(salaryStructure.ctc || 0).toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          </div>

          {/* Salary Components */}
          {salaryStructure.components && salaryStructure.components.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4">
                Salary Components
              </h4>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
                      <tr>
                        <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Component Name
                        </th>
                        <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-3 sm:px-4 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Percentage
                        </th>
                        <th className="px-3 sm:px-4 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(() => {
                        // Calculate totals by type once
                        const earnings = salaryStructure.components
                          .filter(c => c.type === 'earning')
                          .reduce((sum, c) => sum + (c.value || 0), 0);
                        const deductions = salaryStructure.components
                          .filter(c => c.type === 'deduction')
                          .reduce((sum, c) => sum + (c.value || 0), 0);
                        const reimbursements = salaryStructure.components
                          .filter(c => c.type === 'reimbursement')
                          .reduce((sum, c) => sum + (c.value || 0), 0);

                        return (
                          <>
                            {salaryStructure.components.map((component, index) => (
                              <tr key={component._id || component.id || index} className="hover:bg-blue-50/30">
                                <td className="px-3 sm:px-4 py-3 text-sm text-gray-900 capitalize break-words min-w-[120px]">
                                  {component.name || 'N/A'}
                                </td>
                                <td className="px-3 sm:px-4 py-3 text-sm whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${component.type === 'earning'
                                    ? 'bg-green-100 text-green-800'
                                    : component.type === 'deduction'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-blue-100 text-blue-800'
                                    }`}>
                                    {component.type || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-3 sm:px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">
                                  {component.type === 'reimbursement'
                                    ? '—'
                                    : `${component.percentageValue || 0}% CTC`
                                  }
                                </td>
                                <td className="px-3 sm:px-4 py-3 text-sm font-medium text-gray-900 text-right whitespace-nowrap">
                                  ₹{(component.value || 0).toLocaleString('en-IN')}
                                </td>
                              </tr>
                            ))}
                            {/* Show totals after all components */}
                            <tr className="bg-blue-50/20">
                              <td colSpan="3" className="px-3 sm:px-4 py-3 text-sm font-semibold text-gray-900">
                                Total Earnings
                              </td>
                              <td className="px-3 sm:px-4 py-3 text-sm font-semibold text-green-700 text-right whitespace-nowrap">
                                ₹{earnings.toLocaleString('en-IN')}
                              </td>
                            </tr>
                            <tr className="bg-blue-50/20">
                              <td colSpan="3" className="px-3 sm:px-4 py-3 text-sm font-semibold text-gray-900">
                                Total Deductions
                              </td>
                              <td className="px-3 sm:px-4 py-3 text-sm font-semibold text-red-700 text-right whitespace-nowrap">
                                ₹{deductions.toLocaleString('en-IN')}
                              </td>
                            </tr>
                            <tr className="bg-blue-50/20">
                              <td colSpan="3" className="px-3 sm:px-4 py-3 text-sm font-semibold text-gray-900">
                                Total Reimbursements
                              </td>
                              <td className="px-3 sm:px-4 py-3 text-sm font-semibold text-[#0B2033] text-right whitespace-nowrap">
                                ₹{reimbursements.toLocaleString('en-IN')}
                              </td>
                            </tr>
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Net Pay Summary */}
          <div className="bg-blue-50/20 border border-blue-100 rounded-2xl p-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-gray-900">
                Net Pay
              </span>
              <span className="text-2xl font-bold text-brand">
                ₹{(salaryStructure.netPay || 0).toLocaleString('en-IN')}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Net Pay = Earnings + Reimbursements
            </p>
          </div>
        </>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            No salary structure assigned to this {user?.role === 'trainee' ? 'trainee' : 'employee'}.
          </p>
        </div>
      )}

      {/* Bank Details */}
      {payrollData && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">
            Bank Details
          </h4>
          <div className="space-y-2">
            <p className="text-sm text-gray-600 break-words">
              <span className="font-medium">Bank Name:</span>{" "}
              <span className="capitalize">{payrollData.bankName || "N/A"}</span>
            </p>
            <p className="text-sm text-gray-600 break-words">
              <span className="font-medium">Branch Name:</span>{" "}
              <span className="capitalize">{payrollData.bankBranchName || "N/A"}</span>
            </p>
            <p className="text-sm text-gray-600 break-words">
              <span className="font-medium">Account Number:</span> {payrollData.bankAccount || 'N/A'}
            </p>
            <p className="text-sm text-gray-600 break-words">
              <span className="font-medium capitalize">IFSC:</span> {payrollData.ifsc || 'N/A'}
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const tabs = [
    { id: 'personal', label: 'Personal Information', icon: User },
    { id: 'employment', label: 'Employment Information', icon: Building, hideInEdit: true },
    { id: 'compliance', label: 'Compliance', icon: CreditCard },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'salary', label: 'Salary Structure', icon: IndianRupee, hideInEdit: true },
  ];

  const visibleTabs = isEditing ? tabs.filter(t => !t.hideInEdit) : tabs;

  if (isProfileLoading && !profileData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F2942] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profileData && !isProfileLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Profile Not Found</h2>
          <p className="text-gray-600 mb-4">
            Unable to load your profile data. Please contact HR if this issue persists.
          </p>
          <button
            onClick={() => refetchProfile()}
            className="px-4 py-2 bg-[#0F2942] text-white rounded-lg hover:bg-[#0B2033]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] p-4 sm:p-6 relative font-sans" onKeyDown={handleKeyDown}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#0F2942] text-white flex items-center justify-center shadow-sm">
              <User className="w-6 h-6 stroke-[2]" />
            </div>
            <div>
              <h1 className="text-[22px] font-bold text-[#0F2942] border-none leading-tight">My Profile</h1>
              <p className="text-[13px] text-[#64748B] mt-0.5">View and manage your professional profile</p>
            </div>
          </div>
          {!isEditing && (
            <button
              onClick={handleEditClick}
              className="flex items-center space-x-2 px-5 py-2.5 bg-[#0F2942] text-white rounded-lg hover:bg-[#0a1a2a] shadow-sm transition-all active:scale-95"
            >
              <Edit className="w-4 h-4" />
              <span className="font-semibold text-[13px]">Edit Profile</span>
            </button>
          )}
        </div>

        {/* Navigation Tabs (SaaS Segmented Pill Container) */}
        <div className="bg-slate-100/90 p-1.5 rounded-2xl border border-slate-200/80 mb-6 overflow-x-auto no-scrollbar shadow-2xs">
          <nav className="flex space-x-1.5 min-w-max">
            {visibleTabs.map((tab) => {
              const IconComponent = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTabAndSync(tab.id);
                    if (!isEditing) setIsEditing(false);
                  }}
                  className={`py-2.5 px-4 font-bold text-xs sm:text-sm flex items-center space-x-2 rounded-xl whitespace-nowrap transition-all duration-200 ${
                    isActive
                      ? 'bg-white text-blue-700 shadow-sm border border-slate-200/70 scale-[1.01]'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                  }`}
                >
                  <IconComponent className={`w-4 h-4 ${isActive ? 'text-blue-600' : 'text-slate-400'}`} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
          {activeTab === 'personal' && renderPersonalDetails()}
          {activeTab === 'employment' && !isEditing && renderEmploymentDetails()}
          {activeTab === 'compliance' && renderComplianceDetails()}
          {activeTab === 'documents' && renderDocuments()}
          {activeTab === 'salary' && !isEditing && renderSalaryStructure()}
        </div>

        {/* Action Buttons at the bottom of content section */}
        {isEditing && (
          <div className={`mt-6 flex items-center justify-end space-x-3 border-t border-[#E5E7EB] pt-4 ${activeTab === 'documents' ? 'max-w-4xl' : ''}`}>
            <button
              onClick={handleCancel}
              disabled={loading}
              className="px-6 py-2 bg-white border border-[#E5E7EB] text-[#6B7280] rounded-xl hover:bg-gray-50 transition-all font-semibold text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center space-x-2 px-6 py-2 bg-[#0F2942] text-white rounded-xl hover:bg-[#0B2033] shadow-sm transition-all active:scale-95 font-semibold text-sm"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Save className="w-5 h-5" />
              )}
              <span>Save Changes</span>
            </button>
          </div>
        )}

        {/* Change Password Modal */}
        {showChangePassword && renderChangePasswordModal()}
      </div>
    </div>
  );
};

export default MyProfile;
